import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import ChildTabSelector from './ChildTabSelector';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './ParentOnlineClassesView.css';

function ParentOnlineClassesView({ children }) {
  const [selectedChild, setSelectedChild] = useState(null);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const handleChildSelect = (child) => {
    setSelectedChild(child);
  };

  useEffect(() => {
    if (selectedChild) {
      loadOnlineClasses(selectedChild);
    }
  }, [selectedChild]);

  const loadOnlineClasses = async (child) => {
    try {
      setLoading(true);
      setOnlineClasses([]);

      // Fetch online classes for the child's class
      const classId = child.classId;
      if (!classId) {
        console.log('No classId for child:', child);
        setLoading(false);
        return;
      }

      const onlineClassesRef = collection(db, 'classes', classId, 'onlineClasses');
      const snapshot = await getDocs(onlineClassesRef);
      const classes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date (newest first)
      classes.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOnlineClasses(classes);
    } catch (error) {
      console.error('Error loading online classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    // If it's an external meeting link (not Jitsi), open in new tab
    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    // Otherwise, open in-app Jitsi modal
    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = `Parent of ${selectedChild.fullName || 'Student'}`;
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  const canJoin = (onlineClass) => {
    const isOnline = onlineClass.classMode === 'Online' || onlineClass.classMode === 'Hybrid';
    const isActive = onlineClass.status === 'Active' || onlineClass.status === 'active';
    const now = new Date();
    const classDate = new Date(onlineClass.date);
    const isToday = classDate.toDateString() === now.toDateString();
    return isOnline && (isActive || isToday) && getPreferredJoinURL(onlineClass);
  };

  const upcomingClasses = onlineClasses.filter(
    c => new Date(c.date) >= new Date() && c.status !== 'Cancelled' && c.status !== 'cancelled'
  );
  const previousClasses = onlineClasses.filter(
    c => c.status === 'Completed' || c.status === 'completed' || new Date(c.date) < new Date()
  );

  if (!selectedChild) {
    return (
      <div className="parent-online-classes-view">
        <ChildTabSelector children={children} onChildSelect={handleChildSelect} />
      </div>
    );
  }

  return (
    <div className="parent-online-classes-view">
      <ChildTabSelector children={children} onChildSelect={handleChildSelect} selectedChild={selectedChild} />

      {loading ? (
        <div className="loading">Loading online classes...</div>
      ) : onlineClasses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📹</div>
          <h3>No Online Classes</h3>
          <p>Online classes scheduled by teachers will appear here.</p>
        </div>
      ) : (
        <div className="online-classes-container">
          <section className="classes-section">
            <h2>Upcoming Classes</h2>
            {upcomingClasses.length === 0 ? (
              <p className="no-classes">No upcoming classes</p>
            ) : (
              <div className="classes-list">
                {upcomingClasses.map(onlineClass => (
                  <div key={onlineClass.id} className="class-card">
                    <div className="class-header">
                      <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                        {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                      </span>
                      <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                        {onlineClass.status}
                      </span>
                    </div>
                    <h3>{onlineClass.subjectName || 'Class'}</h3>
                    <p className="topic">{onlineClass.topic || 'No topic'}</p>
                    <div className="class-meta">
                      <span>👨‍🏫 {onlineClass.teacherName || 'Teacher'}</span>
                      <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                      <span>⏰ {onlineClass.startTime} - {onlineClass.endTime}</span>
                    </div>
                    {canJoin(onlineClass) && (
                      <button className="join-button" onClick={() => handleJoinClass(onlineClass)}>
                        Join Class
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {previousClasses.length > 0 && (
            <section className="classes-section">
              <h2>Previous Classes</h2>
              <div className="classes-list">
                {previousClasses.slice(0, 10).map(onlineClass => (
                  <div key={onlineClass.id} className="class-card previous">
                    <div className="class-header">
                      <span className={`class-mode ${onlineClass.classMode?.toLowerCase()}`}>
                        {onlineClass.classMode === 'Online' ? '📹' : onlineClass.classMode === 'Hybrid' ? '🔄' : '🏫'}
                      </span>
                      <span className={`class-status ${onlineClass.status?.toLowerCase()}`}>
                        {onlineClass.status}
                      </span>
                    </div>
                    <h3>{onlineClass.subjectName || 'Class'}</h3>
                    <p className="topic">{onlineClass.topic || 'No topic'}</p>
                    <div className="class-meta">
                      <span>👨‍🏫 {onlineClass.teacherName || 'Teacher'}</span>
                      <span>📅 {new Date(onlineClass.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default ParentOnlineClassesView;
